// TODO: generate this validations together
import fileUploadValidations from '@/components/hr-field-upload-validations';
import docSignatureEmbedValidations from '@/components/hr-field-signature-validations';
import {
  DOCUMENT_NAMES,
  WORK_RIGHTS_UK_FIELDS,
  WORK_RIGHTS_UK_VALUES,
  WORK_RIGHTS_AU_FIELDS,
} from '@/core/field-names';
import { filterSuperFieldsToBeIncluded } from '@/store/composables/au/superannuation-rule';
import { filterTFNFieldsToBeIncluded } from '@/store/composables/au/tfn-declaration-rule';
import { filterWorkRightsToBeIncluded as filterWorkRightsToBeIncludedAU } from '@/store/composables/au/right-to-work-rule';
import { filterWorkRightsToBeIncluded as filterWorkRightsToBeIncludedUK } from '@/store/composables/uk/right-to-work-rule';
import { filterTaxDetailsToBeIncluded } from '@/store/composables/uk/tax-details-rule';
import { filterRequiredFieldsToBeIncluded } from '@/store/composables/common/required-fields-rule';
import { checkSignatureStatus } from '@/lib/check-signature-status';
import { COUNTRY } from '@/core/country';
import store from '@/store';

const validations = {};
validations[fileUploadValidations.type] = fileUploadValidations;
validations[docSignatureEmbedValidations.type] = docSignatureEmbedValidations;

// TODO: the logic can be better
// The total completion doesn't take these into account when certain 'value' is selected.
const completionRules = [
  { name: 'superannuation.option', value: 'No', completion: 100 },
];

const ignoreTFNFields = [
  {
    name: 'tfnDeclaration.option',
    value: 'Yes',
    ignoreValue: { name: 'tfnDeclaration.reason' },
  },
  {
    name: 'tfnDeclaration.option',
    value: 'No',
    ignoreValue: { name: 'tfnDeclaration.number' },
  },
];

const specialFields = ['superannuation.type'];
const ignoredFields = ['superannuation.fund'];
const fieldConditionInCompletionRules = [
  {
    checkKey: 'superannuation.type',
    checkValue: null,
    fieldName: 'superannuation.fundMember',
    valueIgnored: 'employeeNominated',
  },
];
const objectTypedFieldNames = [
  'superannuation.fund',
  'personalDetails.emergencyContactMobile',
];

// Adding the additional condition for object type field validation with properties as not empty
function isFieldValueEmpty(value) {
  if (typeof value !== 'object') {
    return false;
  }
  if (value === null) {
    return false;
  }
  return Object.values(value).some(
    (x) => x === null || x === '' || x === undefined
  );
}

export function documentCompletion(document) {
  if (!document || !document.fields) {
    return 0;
  }

  if (document.name in fieldFilterFunctions) {
    if (completionLogicImplementedList.includes(document.name)) {
      document.completion = computeCompletionRate(
        document.name,
        document.fields
      );
    } else {
      document.completion = 0;
    }
    return document;
  }

  return otherDocumentCompletion(document);
}

const completionLogicImplementedList = [
  DOCUMENT_NAMES.TFN_DECLARATION,
  DOCUMENT_NAMES.SUPERANNUATION,
  DOCUMENT_NAMES.ADDITIONAL_QUESTIONS,
  DOCUMENT_NAMES.WORK_RIGHTS,
  DOCUMENT_NAMES.TAX_DETAILS,
  DOCUMENT_NAMES.BANK_DETAILS,
  DOCUMENT_NAMES.PERSONAL_DETAILS,
  DOCUMENT_NAMES.DOC_SIGNATURE_REQUESTS,
  DOCUMENT_NAMES.FILE_UPLOADS,
];

/**
 * Region-aware work rights filter function
 * @param fieldValueMap {Object}
 * @returns {String[]}
 */
export function filterWorkRightsToBeIncluded(fieldValueMap) {
  const countryCode = store.state.onboarding?.countryCode;

  if (countryCode === COUNTRY.AU) {
    return filterWorkRightsToBeIncludedAU(fieldValueMap);
  }

  // Default to UK logic for GB and other regions
  return filterWorkRightsToBeIncludedUK(fieldValueMap);
}

const fieldFilterFunctions = {
  [DOCUMENT_NAMES.TFN_DECLARATION]: filterTFNFieldsToBeIncluded,
  [DOCUMENT_NAMES.SUPERANNUATION]: filterSuperFieldsToBeIncluded,
  [DOCUMENT_NAMES.ADDITIONAL_QUESTIONS]: filterRequiredFieldsToBeIncluded,
  [DOCUMENT_NAMES.WORK_RIGHTS]: filterWorkRightsToBeIncluded,
  [DOCUMENT_NAMES.TAX_DETAILS]: filterTaxDetailsToBeIncluded,
  [DOCUMENT_NAMES.BANK_DETAILS]: filterRequiredFieldsToBeIncluded,
  [DOCUMENT_NAMES.PERSONAL_DETAILS]: filterRequiredFieldsToBeIncluded,
  [DOCUMENT_NAMES.DOC_SIGNATURE_REQUESTS]: filterRequiredFieldsToBeIncluded,
  [DOCUMENT_NAMES.FILE_UPLOADS]: filterRequiredFieldsToBeIncluded,
};

/**
 * TODO: convert backend error into errorFieldsList object
 * @param documentName {String}
 * @param fields {Array}
 * @param errorFieldsList {Object}
 * @returns {number}
 */
export function computeCompletionRate(
  documentName,
  fields,
  errorFieldsList = {}
) {
  const { fieldKeysToBeIncluded, fieldValueMap } = filterWithNameBased({
    documentName,
    fields,
    errorFieldsList,
  });

  const requiredFieldsLength = fieldKeysToBeIncluded.length;

  // case when all document field is optional
  if (requiredFieldsLength === 0 && fields.length !== 0) {
    return 100;
  }

  const correctlyFilled = countFieldsCorrectlyFilled(
    fieldKeysToBeIncluded,
    fieldValueMap
  );

  return (correctlyFilled / requiredFieldsLength) * 100;
}

function filterWithNameBased({ documentName, fields }) {
  const fieldNameValueMap = fields.reduce((prev, field) => {
    const { value, type } = retrieveFieldValue(field);
    let mapFormat = {
      ...prev,
      [field.name]: {
        value,
        type,
        required: field?.validation?.required,

        // fieldError is where we store FE validation error
        hasError: Boolean(field?.fieldError),
      },
    };

    if (documentName === DOCUMENT_NAMES.FILE_UPLOADS) {
      mapFormat[field.name] = {
        ...mapFormat[field.name],
        requiredValues: field.requiredValues,
      };
    }

    if (
      field.name === WORK_RIGHTS_UK_FIELDS.SUPPORTING_DOCUMENTS ||
      field.name === WORK_RIGHTS_UK_FIELDS.PASSPORT ||
      field.name === WORK_RIGHTS_AU_FIELDS.PHOTO_ID
    ) {
      mapFormat[field.name] = {
        ...mapFormat[field.name],
        requiredValues: field.requiredValues,
      };
    }

    return mapFormat;
  }, {});

  const fieldsToBeIncluded =
    fieldFilterFunctions[documentName](fieldNameValueMap);

  return {
    fieldKeysToBeIncluded: fieldsToBeIncluded,
    fieldValueMap: fieldNameValueMap,
  };
}

/**
 *
 * @param includedFieldKeys {String[]}
 * @param fieldValueMap {Object}
 * @param fieldValueMap.value {Any}
 * @param fieldValueMap.required {Boolean}
 * @param fieldValueMap.errorList {String[]}
 * @returns {Number}
 */
export function countFieldsCorrectlyFilled(includedFieldKeys, fieldValueMap) {
  return includedFieldKeys.reduce((prev, key) => {
    const { value, type, hasError, requiredValues } = fieldValueMap[key];

    // We have hasEmptyField here because we haven't implemented fully
    // on FE validation.
    const hasEmptyField = checkValueEmptyFunctions[type](value);

    let areRequiredValuesFilled = true;
    if (requiredValues) {
      areRequiredValuesFilled = checkFileRequiredValues(requiredValues);
    }
    return (
      prev + (!hasEmptyField && !hasError && areRequiredValuesFilled ? 1 : 0)
    );
  }, 0);
}

const checkValueEmptyFunctions = {
  questionValue: isQuestionValueEmpty,
  mobileValue: isMobileValueEmpty,
  fileValue: isFileValueEmpty,
  addressValue: isValueEmpty,
  docSignatureEmbedValue: isDocSignatureNotSigned,
  value: isValueEmpty,
};

function isQuestionValueEmpty(questionValue) {
  if (
    !Array.isArray(questionValue.responses) ||
    !questionValue.responses.length
  ) {
    return true;
  }
  return !questionValue.responses[0].answer;
}

// retrieveFieldValue will map field object with name of the specific type
// and its value.
// e.g. { value: questionValueObjReference, type: 'questionValue' }
function retrieveFieldValue(field) {
  const valueTypes = Object.keys(checkValueEmptyFunctions);
  const foundValueType = valueTypes.find((item) => item in field);
  return {
    value: field[foundValueType],
    type: foundValueType,
  };
}

function isMobileValueEmpty(mobileValue) {
  return !mobileValue.countryCode || !mobileValue.number;
}

function isFileValueEmpty(fileValue) {
  return (
    !fileValue || Object.keys(fileValue).length === 0 || !fileValue.sizeInBytes
  );
}

function checkFileRequiredValues(requiredValues) {
  return Object.values(requiredValues).every((value) => value === true);
}

function isDocSignatureNotSigned(docSignatureValue) {
  const providerData = docSignatureValue?.providerData || {};
  return !checkSignatureStatus(providerData);
}

function isValueEmpty(value) {
  return !value;
}

function otherDocumentCompletion(document) {
  let total = 0;
  let fieldLength = 0;
  let specialRuleApplied = false;
  const docFieldMap = [];
  document.fields.forEach((field) => {
    fieldConditionInCompletionRules.forEach((r) => {
      if (field.name === r.checkKey) {
        r.checkValue = field.value;
      }
    });

    if (!field.validation || (field.validation && !field.validation.required)) {
      return;
    }

    // TODO: Allow to save wrong data?
    // TODO: SHIT logic
    if (
      !field.appliedRules ||
      (field.appliedRules &&
        !Object.keys(field.appliedRules).includes('visible')) ||
      (field.appliedRules && field.appliedRules.visible === true) ||
      (field.defaultValue && field.defaultValueComputed) ||
      // Hack for incorrect Object.keys(appliedRules).includes('visible')
      specialFields.indexOf(field.name) > -1
    ) {
      // Ignore the field `superannuation.fund`
      // because it has been filled up in webapp and the field should be disabled
      if (
        ignoredFields.indexOf(field.name) > -1 &&
        field.validation?.disabled
      ) {
        return;
      }
      // Extra rules for some condition on completion logic
      // It is basically a hack for the first StepCard on landing does not display the completion rate properly
      for (let i = 0; i < fieldConditionInCompletionRules.length; i++) {
        if (
          field.name === fieldConditionInCompletionRules[i].fieldName &&
          fieldConditionInCompletionRules[i].checkValue !==
            fieldConditionInCompletionRules[i].valueIgnored
        ) {
          return;
        }
      }

      fieldLength = fieldLength + 1;
      // accommodate specific rules on generic function + keep backwards compatibility
      // this function modifies the typeValue from GO api to value
      if (validations && validations[field.type]?.getValue) {
        validations[field.type].getValue(field);
      }
      if (
        objectTypedFieldNames.includes(field.name) &&
        isFieldValueEmpty(field.value)
      ) {
        return;
      }
      if (
        field.name === 'superannuation.fund' &&
        field.value === field.defaultValue
      ) {
        return;
      }
      if (field.value) {
        total += 1;
      }
    }
    docFieldMap.push({ name: field.name, value: field.value });
  });

  if (fieldLength === 0) {
    return document;
  }

  // Special rules
  // TODO: make it more flexible
  completionRules.forEach((cr) => {
    docFieldMap.forEach((field) => {
      // When superannuation is "No", completion is 100
      if (cr.name === field.name && cr.value === field.value) {
        document.completion = cr.completion;
        specialRuleApplied = true;
      }
    });
  });

  // TODO: make it generic, currently only works for TFN
  ignoreTFNFields.forEach((igf) => {
    docFieldMap.forEach((field) => {
      let ignoredFields = 0;
      if (igf.name === field.name && igf.value === field.value) {
        ignoredFields++;
        let fieldEmpty = 0;
        if (ignoredFields > 0) {
          // Checking if any other value is empty other than ignoredFieldValues
          document.fields.forEach((field) => {
            if (
              (field.value === null || field.value === '') &&
              igf.ignoreValue.name !== field.name
            ) {
              fieldEmpty++;
            }
          });
          document.completion =
            (100 * (total + ignoredFields - fieldEmpty)) / fieldLength;
          specialRuleApplied = true;
        }
      }
    });
  });

  // Right to work optional uploads logic
  docFieldMap.forEach((field) => {
    if (field.name != WORK_RIGHTS_UK_FIELDS.RESIDENCY) {
      return;
    }
    if (
      field.name == WORK_RIGHTS_UK_FIELDS.RESIDENCY &&
      field.value == WORK_RIGHTS_UK_VALUES.BRITISH_IRISH
    ) {
      const completion = 100 * (total / (fieldLength - 1));
      document.completion = completion > 100 ? 100 : completion;
      specialRuleApplied = true;
    }
  });

  if (false === specialRuleApplied) {
    document.completion = (100 * total) / fieldLength;
  }

  // TODO: This is a quick hack for document becoming negative completion.
  if (document.completion < 0) {
    document.completion = 0;
  }
  return document;
}
